import {
  Block as BlockNoteBlock,
  BlockNoteEditor,
  PartialBlock as BlockNotePartialBlock,
  BlockNoteSchema,
  createHeadingBlockSpec,
  defaultBlockSpecs,
  createImageBlockConfig,
  imageParse,
  type PartialTableContent,
  type SpecificPartialBlock,
} from '@blocknote/core';
import { createReactBlockSpec, ImageToExternalHTML } from '@blocknote/react';
import { z } from 'zod';
import { blockNoteDocumentSchema } from '../../validations/common/block-note-editor';
import { PDF_SAFE_IMAGE_MIME_TYPES } from './helpers/pdf-safe-image-for-embedding';
import ImageBlock from './components/ImageBlock';

const heading = createHeadingBlockSpec({
  allowToggleHeadings: false,
  levels: [1, 2, 3],
});

const imageBlock = createReactBlockSpec(createImageBlockConfig, (config) => ({
  meta: {
    fileBlockAccept: PDF_SAFE_IMAGE_MIME_TYPES,
  },
  runsBefore: ['file'],
  render: ImageBlock,
  parse: imageParse(config),
  toExternalHTML: ImageToExternalHTML,
}));

export const schema = BlockNoteSchema.create({
  blockSpecs: {
    heading,
    paragraph: defaultBlockSpecs.paragraph,
    bulletListItem: defaultBlockSpecs.bulletListItem,
    numberedListItem: defaultBlockSpecs.numberedListItem,
    table: defaultBlockSpecs.table,
    image: imageBlock(),
  },
});

export type BlockSchema = typeof schema.blockSchema;

export type FurtherBlockNoteExtensionContext = {
  editor: BlockNoteEditor<BlockSchema>;
};

export type Block = BlockNoteBlock<BlockSchema>;
export type PartialBlock = BlockNotePartialBlock<BlockSchema>;
export type BlockNoteDocument = z.infer<typeof blockNoteDocumentSchema>;

export type InlineContentSchemaType = typeof schema.inlineContentSchema;
export type StyleSchemaType = typeof schema.styleSchema;

export type EditorPartialTableContent = PartialTableContent<
  InlineContentSchemaType,
  StyleSchemaType
>;
export type EditorPartialTableRow = EditorPartialTableContent['rows'][number];
export type EditorTableCellContent = EditorPartialTableRow['cells'][number];

export type EditorPartialTableBlock = SpecificPartialBlock<
  BlockSchema,
  InlineContentSchemaType,
  'table',
  StyleSchemaType
>;
